import React, { useMemo } from 'react';
import styled from 'styled-components';

import { ConfidenceBand, normaliseConfidenceBand } from 'containers/Intelligence/data/confidence';
import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import useDefaultCurrency from 'hooks/useDefaultCurrency';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { humaniseReasonCodes } from '../answerConfidence';
import {
  formatMetricValue,
  orbitResultFormat,
  orbitMetricName,
  resolveOrbitChart,
  seriesOf
} from '../chartAdapters';
import { TOrbit } from '../i18n/TModules';
import { useOrbitPaging } from '../pagination';
import { OrbitDataTable, orbitTokens, orbitType } from '../styles';
import { OrbitMetricResult, OrbitVisualization } from '../types';
import OrbitChart from './OrbitChart';
import OrbitCohortSummary from './OrbitCohortSummary';
import OrbitPagingFooter from './OrbitPagingFooter';
import OrbitSeriesTable from './OrbitSeriesTable';

export interface IOrbitMetricResultsProps {
  metricResults: OrbitMetricResult[];
  visualization: OrbitVisualization;
}

const Root = styled.div`
  > * + * {
    margin-top: 12px;
  }
`;

const Detail = styled.div`
  margin-top: 2px;
  color: ${orbitTokens.textMuted};
  ${orbitType.helping}
`;

const Muted = styled.span`
  color: ${orbitTokens.textMuted};
`;

/** The band words the two Intelligence screens already publish, so Orbit says `High`, not `high`. */
const BAND_COPY_KEY: Record<ConfidenceBand, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  insufficient: 'Insufficient'
};

const OrbitMetricResults: React.FC<IOrbitMetricResultsProps> = ({
  metricResults,
  visualization
}) => {
  const { t } = useMultiLanguageTranslation();
  const currency = useDefaultCurrency() ?? undefined;
  const chartModel = useMemo(
    () => resolveOrbitChart(visualization, metricResults, currency),
    [visualization, metricResults, currency]
  );
  // Every metric that carries a series gets its own table: only the first is ever charted, so the
  // rest would otherwise be delivered and never shown.
  const distributions = useMemo(
    () => metricResults.filter(result => seriesOf(result).length > 0),
    [metricResults]
  );
  const paging = useOrbitPaging(metricResults.length);

  if (metricResults.length === 0) {
    return null;
  }

  const renderValue = (result: OrbitMetricResult): React.ReactNode => {
    if (result.value !== null) {
      return formatMetricValue(
        result.value,
        orbitResultFormat(result.metric_id, result.cohort),
        currency
      );
    }

    // A distribution metric carries its data in `series`, so the scalar cell is a dash; a metric with
    // neither is genuinely empty and says so — a `0` here would read as a measurement.
    return (
      <Muted>
        {seriesOf(result).length > 0
          ? t(`${TOrbit}.metrics.noValue`)
          : t(`${TOrbit}.metrics.noData`)}
      </Muted>
    );
  };

  const renderConfidence = (result: OrbitMetricResult): React.ReactNode => {
    if (result.confidence === null) {
      return null;
    }

    const band = normaliseConfidenceBand(result.confidence.level);
    const level =
      band === null
        ? result.confidence.level
        : t(`${TCustomerInsights}.ConfidenceGovernance.Bands.${BAND_COPY_KEY[band]}`);

    return (
      <Detail data-testid={`orbit-metric-confidence-${result.metric_id}`}>
        {t(`${TOrbit}.answer.confidence`)}: {level}
      </Detail>
    );
  };

  const renderReasons = (result: OrbitMetricResult): React.ReactNode => {
    if (result.why_this_result === null) {
      return null;
    }

    const reasons = humaniseReasonCodes(result.why_this_result.reason_codes);

    if (reasons.length === 0) {
      return null;
    }

    return (
      <Detail data-testid={`orbit-metric-reasons-${result.metric_id}`}>
        {t(`${TOrbit}.metrics.reasonCodes`)}: {reasons.join(', ')}
      </Detail>
    );
  };

  return (
    <Root data-testid="orbit-metric-results">
      {metricResults.map(result =>
        result.cohort ? (
          <OrbitCohortSummary
            key={result.metric_id}
            metricId={result.metric_id}
            cohort={result.cohort}
          />
        ) : null
      )}
      <OrbitDataTable>
        <thead>
          <tr>
            <th scope="col">{t(`${TOrbit}.metrics.columnMetric`)}</th>
            <th scope="col">{t(`${TOrbit}.metrics.columnValue`)}</th>
          </tr>
        </thead>
        <tbody>
          {metricResults.slice(0, paging.shown).map(result => (
            <tr key={result.metric_id} data-testid={`orbit-metric-row-${result.metric_id}`}>
              {/*
                The catalogue NAME, with the id only on the title attribute: the id is what Support
                needs to trace a reading and what a reader should never have to decode (PLA-9563 §3a).
              */}
              <th scope="row" title={result.metric_id}>
                {orbitMetricName(result.metric_id, result.metric_name)}
                {renderConfidence(result)}
                {renderReasons(result)}
              </th>
              <td data-testid={`orbit-metric-value-${result.metric_id}`}>{renderValue(result)}</td>
            </tr>
          ))}
        </tbody>
      </OrbitDataTable>
      <OrbitPagingFooter paging={paging} testId="orbit-metric-paging" />
      {chartModel !== null ? <OrbitChart model={chartModel} /> : null}
      {distributions.map(result => (
        <OrbitSeriesTable key={result.metric_id} result={result} currency={currency} />
      ))}
    </Root>
  );
};

export default OrbitMetricResults;
